import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import './AdminPanel.css';
import { 
  FaPlus, FaMinus, FaSearch, FaCheck, FaTimes, 
  FaCoins, FaUsers, FaExchangeAlt, FaSpinner,
  FaSignOutAlt, FaUserShield, FaFilter, FaSort, FaDownload, FaClock,
  FaEdit, FaSave, FaTrash, FaThLarge, FaList, FaHistory
} from 'react-icons/fa';

// Separate Stats component
const Stats = React.memo(({ users, transactions }) => {
  const stats = {
    totalUsers: users.length,
    totalCredits: users.reduce((sum, user) => sum + user.credits, 0),
    totalPremiumCredits: users.reduce((sum, user) => sum + (user.premiumCredits || 0), 0),
    pendingTransactions: transactions.filter(t => t.status === 'pending').length
  };

  return (
    <div className="stats-grid">
      <div className="stat-card">
        <FaUsers size={24} color="#2e7d32" />
        <div className="stat-value">{stats.totalUsers}</div>
        <div className="stat-label">Total Users</div>
      </div>
      <div className="stat-card">
        <FaCoins size={24} color="#2e7d32" />
        <div className="stat-value">{stats.totalCredits}</div>
        <div className="stat-label">Free Credits</div>
      </div>
      <div className="stat-card premium">
        <FaCoins size={24} color="#8a2be2" />
        <div className="stat-value">{stats.totalPremiumCredits}</div>
        <div className="stat-label">Premium Credits</div>
      </div>
      <div className="stat-card">
        <FaExchangeAlt size={24} color="#2e7d32" />
        <div className="stat-value">{stats.pendingTransactions}</div>
        <div className="stat-label">Pending Transactions</div>
      </div>
    </div>
  );
});

// Separate Dashboard component
const DashboardTab = React.memo(({ users, transactions, onTabChange }) => {
  return (
    <div className="tab-content">
      <Stats users={users} transactions={transactions} />
      <div className="card-grid">
        <div className="quick-actions-card admin-section">
          <h3>Quick Actions</h3>
          <button className="admin-button" onClick={() => onTabChange('users')}>
            <FaUsers /> Manage Users
          </button>
          <button className="admin-button" onClick={() => onTabChange('transactions')}>
            <FaExchangeAlt /> View Transactions
          </button>
        </div>
        <div className="recent-activity-card admin-section">
          <h3>Recent Activity</h3>
          {transactions.slice(0, 5).map(transaction => (
            <div key={transaction._id} className="activity-item">
              <span>{new Date(transaction.createdAt).toLocaleDateString()}</span>
              <span>{transaction.email}</span>
              <span className={`status-badge status-${transaction.status}`}>
                {transaction.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

// Add this new component near the top with other components
const CreateUserModal = React.memo(({ isOpen, onClose, onCreateUser }) => {
  const [newUserEmail, setNewUserEmail] = useState('');
  const [initialCredits, setInitialCredits] = useState(0);
  const [initialPremiumCredits, setInitialPremiumCredits] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onCreateUser(newUserEmail, initialCredits, initialPremiumCredits);
      // Reset form
      setNewUserEmail('');
      setInitialCredits(0);
      setInitialPremiumCredits(0);
      onClose();
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h3>Create New User</h3>
        <form onSubmit={handleSubmit} className="create-user-form">
          <div className="input-group">
            <label>Email:</label>
            <input
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="User Email"
              required
              className="text-input"
            />
          </div>
          <div className="input-group">
            <label>Initial Credits:</label>
            <input
              type="number"
              value={initialCredits}
              onChange={(e) => setInitialCredits(Number(e.target.value))}
              min="0"
              className="text-input"
            />
          </div>
          <div className="input-group">
            <label>Initial Premium Credits:</label>
            <input
              type="number"
              value={initialPremiumCredits}
              onChange={(e) => setInitialPremiumCredits(Number(e.target.value))}
              min="0"
              className="text-input"
            />
          </div>
          <div className="modal-actions">
            <button type="submit" className="admin-button success">
              <FaPlus /> Create User
            </button>
            <button type="button" onClick={onClose} className="admin-button secondary">
              <FaTimes /> Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
});

// Separate UsersTab component
const UsersTab = React.memo(({ 
  users, 
  searchEmail, 
  onSearchChange, 
  onUpdateCredits, 
  onDeleteUser,
  onCreateUser,
  adminToken 
}) => {
  const [editingUserId, setEditingUserId] = useState(null);
  const [editCreditsValue, setEditCreditsValue] = useState(0);
  const [editPremiumCreditsValue, setEditPremiumCreditsValue] = useState(0);
  const [userView, setUserView] = useState('grid');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const filteredUsers = useMemo(() => {
    return users.filter(user => 
      user.email.toLowerCase().includes(searchEmail.toLowerCase())
    );
  }, [users, searchEmail]);

  const handleEditCredits = async (userId, email, currentCredits, currentPremiumCredits) => {
    if (editingUserId === userId) {
      try {
        await onUpdateCredits(email, editCreditsValue, editPremiumCreditsValue);
        setEditingUserId(null);
        setEditCreditsValue(0);
        setEditPremiumCreditsValue(0);
      } catch (error) {
        console.error('Failed to update credits:', error);
      }
    } else {
      setEditingUserId(userId);
      setEditCreditsValue(currentCredits);
      setEditPremiumCreditsValue(currentPremiumCredits || 0);
    }
  };

  return (
    <div className="tab-content">
      <div className="section-header">
        <h2><FaUsers /> Users Management</h2>
        <div className="header-actions">
          <button 
            className="admin-button success"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <FaPlus /> Create User
          </button>
          <div className="view-controls">
            <button 
              className={`admin-button small ${userView === 'grid' ? 'active' : ''}`}
              onClick={() => setUserView('grid')}
            >
              <FaThLarge /> Grid View
            </button>
            <button 
              className={`admin-button small ${userView === 'table' ? 'active' : ''}`}
              onClick={() => setUserView('table')}
            >
              <FaList /> Table View
            </button>
          </div>
        </div>
      </div>

      <div className="search-bar">
        <FaSearch className="search-icon" />
        <input
          type="text"
          value={searchEmail}
          onChange={(e) => onSearchChange(e)}
          placeholder="Search users by email..."
          className="text-input"
        />
      </div>

      {/* Add the CreateUserModal */}
      <CreateUserModal 
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateUser={onCreateUser}
      />

      {userView === 'grid' ? (
        <div className="card-grid">
          {filteredUsers.map(user => (
            <div key={user._id} className="user-card">
              <div className="user-card-header">
                <span className="user-card-email">{user.email}</span>
                <div className="user-card-credits-container">
                  <div className="user-card-credits">
                    <FaCoins color="#2e7d32" />
                    {editingUserId === user._id ? (
                      <input
                        type="number"
                        value={editCreditsValue}
                        onChange={(e) => setEditCreditsValue(Number(e.target.value))}
                        className="credits-input"
                        autoFocus
                      />
                    ) : (
                      <span>{user.credits} <small>Free</small></span>
                    )}
                  </div>
                  <div className="user-card-credits premium">
                    <FaCoins color="#8a2be2" />
                    {editingUserId === user._id ? (
                      <input
                        type="number"
                        value={editPremiumCreditsValue}
                        onChange={(e) => setEditPremiumCreditsValue(Number(e.target.value))}
                        className="credits-input premium"
                      />
                    ) : (
                      <span>{user.premiumCredits || 0} <small>Premium</small></span>
                    )}
                  </div>
                </div>
              </div>
              <div className="user-card-actions">
                {editingUserId === user._id ? (
                  <>
                    <button 
                      className="admin-button small success"
                      onClick={() => handleEditCredits(user._id, user.email, user.credits, user.premiumCredits)}
                    >
                      <FaSave /> Save
                    </button>
                    <button 
                      className="admin-button small secondary"
                      onClick={() => setEditingUserId(null)}
                    >
                      <FaTimes /> Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      className="admin-button small"
                      onClick={() => handleEditCredits(user._id, user.email, user.credits, user.premiumCredits)}
                    >
                      <FaEdit /> Edit Credits
                    </button>
                    <button 
                      className="admin-button small danger"
                      onClick={() => onDeleteUser(user.email)}
                    >
                      <FaTrash /> Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Free Credits</th>
                <th>Premium Credits</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user._id}>
                  <td>{user.email}</td>
                  <td>
                    {editingUserId === user._id ? (
                      <input
                        type="number"
                        value={editCreditsValue}
                        onChange={(e) => setEditCreditsValue(Number(e.target.value))}
                        className="credits-input"
                        autoFocus
                      />
                    ) : (
                      user.credits
                    )}
                  </td>
                  <td>
                    {editingUserId === user._id ? (
                      <input
                        type="number"
                        value={editPremiumCreditsValue}
                        onChange={(e) => setEditPremiumCreditsValue(Number(e.target.value))}
                        className="credits-input premium"
                      />
                    ) : (
                      user.premiumCredits || 0
                    )}
                  </td>
                  <td>
                    {editingUserId === user._id ? (
                      <>
                        <button 
                          className="admin-button small success"
                          onClick={() => handleEditCredits(user._id, user.email, user.credits, user.premiumCredits)}
                        >
                          <FaSave />
                        </button>
                        <button 
                          className="admin-button small secondary"
                          onClick={() => setEditingUserId(null)}
                        >
                          <FaTimes />
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          className="admin-button small"
                          onClick={() => handleEditCredits(user._id, user.email, user.credits, user.premiumCredits)}
                        >
                          <FaEdit />
                        </button>
                        <button 
                          className="admin-button small danger"
                          onClick={() => onDeleteUser(user.email)}
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
});

// Separate TransactionsTab component
const TransactionsTab = React.memo(({ 
  transactions, 
  onVerifyTransaction, 
  searchTerm, 
  onSearchChange 
}) => {
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [regularCreditsToAdd, setRegularCreditsToAdd] = useState(0);
  const [premiumCreditsToAdd, setPremiumCreditsToAdd] = useState(0);

  const filteredTransactions = useMemo(() => {
    return transactions.filter(transaction => {
      const matchesSearch = 
        searchTerm === '' || 
        transaction.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.transactionId.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = 
        selectedStatus === 'all' || 
        transaction.status === selectedStatus;
      
      return matchesSearch && matchesStatus;
    });
  }, [transactions, searchTerm, selectedStatus]);

  const handleStatusFilterChange = (status) => {
    setSelectedStatus(status);
  };

  const handleOpenApprovalModal = (transaction) => {
    setSelectedTransaction(transaction);
    setRegularCreditsToAdd(transaction.creditsAmount || 0);
    setPremiumCreditsToAdd(0);
    setShowApprovalModal(true);
  };

  const handleApproveTransaction = async () => {
    if (selectedTransaction) {
      await onVerifyTransaction(
        selectedTransaction._id, 
        true, 
        regularCreditsToAdd,
        premiumCreditsToAdd
      );
      setShowApprovalModal(false);
      setSelectedTransaction(null);
    }
  };

  const handleRejectTransaction = async () => {
    if (selectedTransaction) {
      await onVerifyTransaction(selectedTransaction._id, false);
      setShowApprovalModal(false);
      setSelectedTransaction(null);
    }
  };

  return (
    <div className="tab-content">
      <div className="section-header">
        <h2><FaExchangeAlt /> Transactions</h2>
        <div className="filter-controls">
          <button 
            className={`filter-button ${selectedStatus === 'all' ? 'active' : ''}`}
            onClick={() => handleStatusFilterChange('all')}
          >
            All
          </button>
          <button 
            className={`filter-button ${selectedStatus === 'pending' ? 'active' : ''}`}
            onClick={() => handleStatusFilterChange('pending')}
          >
            Pending
          </button>
          <button 
            className={`filter-button ${selectedStatus === 'approved' ? 'active' : ''}`}
            onClick={() => handleStatusFilterChange('approved')}
          >
            Approved
          </button>
          <button 
            className={`filter-button ${selectedStatus === 'rejected' ? 'active' : ''}`}
            onClick={() => handleStatusFilterChange('rejected')}
          >
            Rejected
          </button>
        </div>
      </div>

      <div className="search-bar">
        <FaSearch className="search-icon" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => onSearchChange(e)}
          placeholder="Search by email or transaction ID..."
          className="text-input"
        />
      </div>

      <div className="table-container">
        <table className="admin-table transactions-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Email</th>
              <th>BTC Amount</th>
              <th>USD Value</th>
              <th>Credits</th>
              <th>Transaction ID</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.map(transaction => (
              <tr key={transaction._id} className={`status-${transaction.status}`}>
                <td>{new Date(transaction.createdAt).toLocaleDateString()}</td>
                <td>{transaction.email}</td>
                <td>{transaction.btcAmount}</td>
                <td>${parseFloat(transaction.usdAmount).toFixed(2)}</td>
                <td>{transaction.creditsAmount}</td>
                <td>
                  <a 
                    href={`https://mempool.space/tx/${transaction.transactionId}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="transaction-link"
                  >
                    {transaction.transactionId.substring(0, 12)}...
                  </a>
                </td>
                <td>
                  <span className={`status-badge status-${transaction.status}`}>
                    {transaction.status}
                  </span>
                </td>
                <td>
                  {transaction.status === 'pending' && (
                    <div className="button-group">
                      <button 
                        className="admin-button small success"
                        onClick={() => handleOpenApprovalModal(transaction)}
                      >
                        <FaCheck /> 
                      </button>
                      <button 
                        className="admin-button small danger"
                        onClick={() => onVerifyTransaction(transaction._id, false)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Transaction Approval Modal */}
      {showApprovalModal && selectedTransaction && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Approve Transaction</h3>
            <div className="transaction-details">
              <p><strong>Email:</strong> {selectedTransaction.email}</p>
              <p><strong>BTC Amount:</strong> {selectedTransaction.btcAmount}</p>
              <p><strong>USD Value:</strong> ${parseFloat(selectedTransaction.usdAmount).toFixed(2)}</p>
              <p><strong>Transaction ID:</strong> {selectedTransaction.transactionId}</p>
            </div>

            <div className="credit-allocation">
              <h4>Credit Allocation</h4>
              
              <div className="input-group">
                <label>Regular Credits:</label>
                <input
                  type="number"
                  value={regularCreditsToAdd}
                  onChange={(e) => setRegularCreditsToAdd(Number(e.target.value))}
                  min="0"
                  className="text-input"
                />
                <p className="input-hint">Default amount based on USD value</p>
              </div>
              
              <div className="input-group">
                <label>Premium Credits:</label>
                <input
                  type="number"
                  value={premiumCreditsToAdd}
                  onChange={(e) => setPremiumCreditsToAdd(Number(e.target.value))}
                  min="0"
                  className="text-input premium"
                />
                <p className="input-hint">Premium credits with no watermark</p>
              </div>
            </div>

            <div className="modal-actions">
              <button className="admin-button success" onClick={handleApproveTransaction}>
                <FaCheck /> Approve
              </button>
              <button className="admin-button danger" onClick={handleRejectTransaction}>
                <FaTimes /> Reject
              </button>
              <button className="admin-button secondary" onClick={() => setShowApprovalModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

// Add this new component after other tab components
const CreditHistoryTab = React.memo(({ adminToken }) => {
  const [creditHistory, setCreditHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchEmail, setSearchEmail] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');

  const fetchCreditHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://frogland.onrender.com/admin/credit-history${searchEmail ? `?email=${searchEmail}` : ''}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      setCreditHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch credit history:', error);
    } finally {
      setLoading(false);
    }
  }, [adminToken, searchEmail]);

  useEffect(() => {
    fetchCreditHistory();
  }, [fetchCreditHistory]);

  const processedHistory = useMemo(() => {
    return [...creditHistory].sort((a, b) => {
      if (sortOrder === 'newest') return new Date(b.timestamp) - new Date(a.timestamp);
      return new Date(a.timestamp) - new Date(b.timestamp);
    });
  }, [creditHistory, sortOrder]);

  return (
    <div className="tab-content">
      <div className="section-header">
        <h2><FaHistory /> Credit History</h2>
      </div>

      <div className="filter-controls">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            placeholder="Filter by email..."
            className="text-input"
          />
        </div>

        <select 
          className="filter-select"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <FaSpinner className="fa-spin" />
        </div>
      ) : (
        <div className="credit-history-list">
          {processedHistory.map(record => (
            <div key={record._id} className="history-card">
              <div className="history-header">
                <span className="history-date">
                  <FaClock /> {new Date(record.timestamp).toLocaleString()}
                </span>
                <span className="history-credits">
                  <FaCoins /> {record.credits > 0 ? '+' : ''}{record.credits} Credits
                </span>
              </div>
              
              <div className="history-details">
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{record.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Source</span>
                  <span className="detail-value">{record.source}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Reason</span>
                  <span className="detail-value">{record.reason}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

function AdminPanel() {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState('');
  const [credits, setCredits] = useState(0);
  const [searchEmail, setSearchEmail] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editCredits, setEditCredits] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [adminToken, setAdminToken] = useState(localStorage.getItem('adminToken'));
  const [activeTab, setActiveTab] = useState('dashboard');
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [userView, setUserView] = useState('grid'); // 'grid' or 'table'
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);  // Move this to main component

  // Memoize filtered users
  const filteredUsers = useMemo(() => {
    return users.filter(user => 
      user.email.toLowerCase().includes(searchEmail.toLowerCase())
    );
  }, [users, searchEmail]); // Only recompute when users or searchEmail changes

  // Memoize filtered and sorted transactions
  const processedTransactions = useMemo(() => {
    let filtered = transactions;
    if (transactionFilter !== 'all') {
      filtered = transactions.filter(t => t.status === transactionFilter);
    }
    
    return [...filtered].sort((a, b) => {
      if (sortOrder === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
      return new Date(a.createdAt) - new Date(b.createdAt);
    });
  }, [transactions, transactionFilter, sortOrder]);

  // Memoize pending count
  const pendingCount = useMemo(() => {
    return transactions.filter(t => t.status === 'pending').length;
  }, [transactions]);

  // Memoize handlers using useCallback
  const handleAdminLogin = useCallback(async (e) => {
    e.preventDefault();
    try {
      console.log('Attempting admin login...');
      const loginResponse = await axios.post('https://frogland.onrender.com/admin/login', {
        email: adminEmail,
        password: adminPassword
      });

      if (loginResponse.data.success) {
        const token = loginResponse.data.token;
        console.log('Login successful, token received');
        
        setAdminToken(token);
        localStorage.setItem('adminToken', token);
        
        setIsAuthenticated(true);
        
        await Promise.all([
          fetchUsers(token),
          fetchTransactions(token)
        ]);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Invalid admin credentials');
    }
  }, [adminEmail, adminPassword]);

  const fetchUsers = useCallback(async (token = adminToken) => {
    try {
      const response = await axios.get('https://frogland.onrender.com/admin/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setUsers(response.data);
    } catch (error) {
      setError('Failed to fetch users');
    }
  }, [adminToken]);

  const handleAddCredits = async (e) => {
    e.preventDefault();
    try {
      await axios.post('https://frogland.onrender.com/admin/add-credits', {
        email,
        credits: Number(credits)
      }, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      setSuccess(`Successfully added ${credits} credits to ${email}`);
      setEmail('');
      setCredits(0);
      fetchUsers(adminToken);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add credits');
    }
  };

  const handleUpdateCredits = async (email, newCredits, newPremiumCredits) => {
    try {
      const response = await axios.put(
        `https://frogland.onrender.com/admin/user/${email}/credits`,
        {
          credits: newCredits,
          premiumCredits: newPremiumCredits
        },
        {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        }
      );

      if (response.data.success) {
        setSuccess(`Successfully updated user's credits to ${newCredits} free and ${newPremiumCredits} premium`);
        fetchUsers(); // Refresh the users list
      }
    } catch (error) {
      setError(`Failed to update credits: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteUser = async (email) => {
    if (window.confirm(`Are you sure you want to delete user ${email}?`)) {
      try {
        await axios.delete(`https://frogland.onrender.com/admin/delete-user/${email}`, {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        });
        fetchUsers(adminToken);
        setSuccess(`Successfully deleted user ${email}`);
      } catch (error) {
        setError('Failed to delete user');
      }
    }
  };

  const fetchTransactions = useCallback(async (token = adminToken) => {
    try {
      console.log('Fetching transactions with token:', token);
      
      if (!token) {
        console.log('No token available');
        return;
      }

      setTransactionLoading(true);
      const response = await axios.get('https://frogland.onrender.com/admin/transactions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Transactions received:', response.data);
      setTransactions(response.data);
    } catch (error) {
      console.error('Fetch transactions error:', error.response || error);
      setError(error.response?.data?.message || 'Error fetching transactions');
    } finally {
      setTransactionLoading(false);
    }
  }, [adminToken]);

  const handleVerifyTransaction = async (transactionId, approved, regularCredits, premiumCredits) => {
    try {
      setTransactionLoading(true);
      
      const response = await axios.post(
        'https://frogland.onrender.com/admin/verify-transaction',
        {
          transactionId,
          approved,
          credits: regularCredits,
          premiumCredits
        },
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );
      
      if (response.data.success) {
        setSuccess(`Transaction successfully ${approved ? 'approved' : 'rejected'}`);
        fetchTransactions(adminToken);
        fetchUsers(adminToken);
      }
    } catch (error) {
      setError(`Failed to process transaction: ${error.response?.data?.message || error.message}`);
    } finally {
      setTransactionLoading(false);
    }
  };

  const handleLogout = () => {
    setAdminToken(null);
    setIsAuthenticated(false);
    localStorage.removeItem('adminToken');
  };

  // Optimize useEffect
  useEffect(() => {
    if (isAuthenticated && adminToken) {
      // Initial fetch
      fetchTransactions();
      fetchUsers();

      // Set up polling interval
      const interval = setInterval(() => {
        fetchTransactions();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, adminToken, fetchTransactions, fetchUsers]);

  // Memoize search input handler
  const handleSearchChange = useCallback((e) => {
    setSearchEmail(e.target.value);
  }, []);

  // Memoize filter handlers
  const handleFilterChange = useCallback((e) => {
    setTransactionFilter(e.target.value);
  }, []);

  const handleSortChange = useCallback((e) => {
    setSortOrder(e.target.value);
  }, []);

  const handleCreateUser = async (email, initialCredits, initialPremiumCredits) => {
    try {
      // First create the user
      const response = await axios.post(
        'https://frogland.onrender.com/admin/add-credits',
        {
          email,
          credits: initialCredits,
          premiumCredits: initialPremiumCredits
        },
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      setSuccess(`Successfully created user ${email} with ${initialCredits} credits and ${initialPremiumCredits} premium credits`);
      fetchUsers(); // Refresh the users list
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create user');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-container">
        <div className="admin-section">
          <h2>Admin Login</h2>
          <form onSubmit={handleAdminLogin} className="admin-form">
            <div className="input-group">
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="Admin Email"
                required
                className="text-input"
              />
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="Password"
                required
                className="text-input"
              />
              <button type="submit" className="admin-button">
                Login
              </button>
            </div>
          </form>
          {error && <div className="error-message">{error}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div className="admin-header-left">
          <h1><FaUserShield /> Admin Panel</h1>
        </div>
        <div className="admin-header-actions">
          <button 
            className="admin-button success"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <FaPlus /> Create User
          </button>
          <button onClick={handleLogout} className="admin-button danger">
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </div>

      {/* Add CreateUserModal here, outside of any tab */}
      <CreateUserModal 
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateUser={handleCreateUser}
      />

      <div className="tabs-container">
        <div className="tabs-header">
          <button 
            className={`tab-button ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
            <button 
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
            >
            <FaUsers /> Users
            </button>
            <button 
            className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
            >
            <FaExchangeAlt /> Transactions
            {pendingCount > 0 && (
              <span className="tab-badge">{pendingCount}</span>
            )}
            </button>
            <button 
            className={`tab-button ${activeTab === 'credit-history' ? 'active' : ''}`}
            onClick={() => setActiveTab('credit-history')}
          >
            <FaHistory /> Credit History
            </button>
          </div>

        {error && <div className="error-message"><FaTimes /> {error}</div>}
        {success && <div className="success-message"><FaCheck /> {success}</div>}

        {activeTab === 'dashboard' && (
          <DashboardTab 
            users={users} 
            transactions={transactions} 
            onTabChange={setActiveTab} 
          />
        )}
        {activeTab === 'users' && (
          <UsersTab 
            users={users}
            searchEmail={searchEmail}
            onSearchChange={handleSearchChange}
            onUpdateCredits={handleUpdateCredits}
            onDeleteUser={handleDeleteUser}
            onCreateUser={handleCreateUser}
            adminToken={adminToken}
          />
        )}
        {activeTab === 'transactions' && (
          <TransactionsTab 
            transactions={transactions}
            onVerifyTransaction={handleVerifyTransaction}
            searchTerm={searchEmail}
            onSearchChange={handleSearchChange}
          />
        )}
        {activeTab === 'credit-history' && (
          <CreditHistoryTab adminToken={adminToken} />
        )}
      </div>
    </div>
  );
}

export default React.memo(AdminPanel); 